/**
 * Table of Contents Hook
 *
 * Tracks scroll position and provides navigation for note sections
 */

import { useCallback, useEffect, useState } from 'react';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

export interface TOCSection {
  section_id: string;
  name: string;
}

export interface UseNoteTOCReturn {
  /** Currently active section ID (based on scroll position) */
  activeSectionId: string | null;
  /** Scroll to a specific section smoothly */
  scrollToSection: (sectionId: string) => void;
}

/**
 * Hook for managing Table of Contents functionality
 *
 * Features:
 * - Tracks which section is currently visible using Intersection Observer
 * - Provides smooth scroll navigation to sections
 * - Handles edge cases (no sections, scrolling)
 *
 * @param sections - Array of note sections to track
 * @param containerSelector - CSS selector for the scrollable container (default: scrolls main viewport)
 * @returns Active section ID and navigation function
 */
export const useNoteTOC = (
  sections: TOCSection[],
  containerSelector?: string,
): UseNoteTOCReturn => {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  /**
   * Smooth scroll to a specific section
   */
  const scrollToSection = useCallback(
    (sectionId: string) => {
      // Find the section element by data attribute
      const element = document.querySelector(`[data-section-id="${sectionId}"]`);

      if (element) {
        // Get the scrollable container or use window
        const container = containerSelector ? document.querySelector(containerSelector) : null;

        if (container) {
          // Calculate scroll position relative to container
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const scrollTop = container.scrollTop;
          const offset = elementRect.top - containerRect.top + scrollTop;

          // Smooth scroll within container
          container.scrollTo({
            top: offset - LAYOUT_DIMENSIONS.SCROLL_OFFSET,
            behavior: 'smooth',
          });
        } else {
          // Fallback to window scroll
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    },
    [containerSelector],
  );

  /**
   * Set up Intersection Observer to track active section
   */
  useEffect(() => {
    if (sections.length === 0) {
      setActiveSectionId(null);
      return;
    }

    // Find all section elements by data attribute
    const sectionElements = sections
      .map(section => document.querySelector(`[data-section-id="${section.section_id}"]`))
      .filter((el): el is Element => el !== null);

    if (sectionElements.length === 0) {
      return;
    }

    // Track which sections are currently intersecting
    const intersectingMap = new Map<string, boolean>();

    const observer = new IntersectionObserver(
      entries => {
        // Update intersection map
        entries.forEach(entry => {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (sectionId) {
            intersectingMap.set(sectionId, entry.isIntersecting);
          }
        });

        // Find the first intersecting section (topmost in viewport)
        const firstIntersecting = sections.find(section => intersectingMap.get(section.section_id));

        if (firstIntersecting) {
          setActiveSectionId(firstIntersecting.section_id);
        } else {
          // If nothing intersecting, keep the last active section
          // This prevents losing active state when scrolling between sections
        }
      },
      {
        // Trigger when section is in the center band of viewport
        // Negative top margin (-40%) shrinks root from top
        // Negative bottom margin (-40%) shrinks root from bottom
        // This creates a detection zone from 40% to 60% of viewport height
        rootMargin: '-40% 0px -40% 0px',
        threshold: 0,
        root: containerSelector ? document.querySelector(containerSelector) : null,
      },
    );

    // Observe all section elements
    sectionElements.forEach(el => observer.observe(el));

    // Set initial active section (first section by default)
    if (!activeSectionId && sections.length > 0) {
      setActiveSectionId(sections[0].section_id);
    }

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [sections, containerSelector, activeSectionId]);

  return {
    activeSectionId,
    scrollToSection,
  };
};
